import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { inventoryApi } from '@/lib/inventoryApi';
import { toNumber } from '@/lib/number';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertTriangle, ArrowRight, Package } from 'lucide-react';
import type { LowStockItem } from '@/types/inventory';
import type { ApiListResponse } from '@/types/api';

interface LowStockWidgetProps {
  farmId: string;
}

export function LowStockWidget({ farmId }: LowStockWidgetProps) {
  const { data, isLoading, error } = useQuery<ApiListResponse<LowStockItem>>({
    queryKey: ['inventory', 'stock', 'low', farmId],
    queryFn: () => inventoryApi.getLowStock(farmId),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const items = data?.data ?? [];

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            Low Stock
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center justify-center py-6">
            <LoadingSpinner size={20} className="text-gray-400" />
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            Low Stock
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-gray-500 text-center py-4">
            Unable to load stock data
          </p>
        </Card.Body>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-400" />
            Stock Status
          </div>
        </Card.Header>
        <Card.Body>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <div className="rounded-full bg-green-50 p-2 mb-2">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">All stocked up</p>
            <p className="text-xs text-gray-500 mt-0.5">No items below reorder point</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header
        action={
          <Link
            to="/inventory"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        }
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          Low Stock
          <Badge variant="warning" size="sm">
            {items.length}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="px-0 py-0">
        <ul className="divide-y divide-gray-50">
          {items.slice(0, 5).map((item) => (
            <LowStockRow key={item.item_id} item={item} />
          ))}
        </ul>
        {items.length > 5 && (
          <div className="px-5 py-2 text-center border-t border-gray-50">
            <Link
              to="/inventory"
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              +{items.length - 5} more items
            </Link>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function LowStockRow({ item }: { item: LowStockItem }) {
  const qty = toNumber(item.quantity);
  const reorder = toNumber(item.reorder_point);
  const shortage = toNumber(item.shortage);
  const isOutOfStock = qty <= 0;

  return (
    <li className="flex items-center justify-between px-5 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{item.item_name}</p>
        <p className="text-xs text-gray-400">
          {qty.toLocaleString()} / {reorder.toLocaleString()} {item.unit}
        </p>
      </div>
      <div className="ml-3 shrink-0">
        {isOutOfStock ? (
          <Badge variant="critical" size="sm" dot>Out</Badge>
        ) : (
          <Badge variant="warning" size="sm" dot>
            -{shortage.toLocaleString()}
          </Badge>
        )}
      </div>
    </li>
  );
}
